import { Widget } from "@serenity-is/corelib";

export class ImageBase64Editor extends Widget<any> {
    // registration key must match generated attribute Key
    static override[Symbol.typeInfo] = this.registerEditor('VinaSAB.Common.Fields.ImageBase64Editor');

    private root: any;
    private wrapperEl!: HTMLElement;
    private imgEl!: HTMLImageElement;
    private labelEl?: HTMLElement | null;

    constructor(container: any) {
        super(container);
        this.root = container;
        this.buildUi();
    }

    private buildUi(): void {
        const wrapper = document.createElement('div');
        wrapper.className = 'image-base64-editor';
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.justifyContent = 'center';
        wrapper.style.width = '100%';
        wrapper.style.height = '200px';
        wrapper.style.background = '#fafafa';
        wrapper.style.border = '1px solid #e6e6e6';
        wrapper.style.borderRadius = '4px';
        wrapper.style.overflow = 'hidden';

        this.imgEl = document.createElement('img');
        this.imgEl.style.maxWidth = '100%';
        this.imgEl.style.maxHeight = '100%';
        this.imgEl.style.objectFit = 'contain';
        this.imgEl.alt = 'image';
        wrapper.appendChild(this.imgEl);

        // attach wrapper
        try {
            if (this.root instanceof HTMLElement) {
                this.root.appendChild(wrapper);
                this.labelEl = this.findLabelForHost(this.root);
            }
            else if (this.root && typeof this.root.element === 'object' && this.root.element instanceof HTMLElement) {
                this.root.element.appendChild(wrapper);
                this.labelEl = this.findLabelForHost(this.root.element);
            }
            else if (this.root && typeof this.root.append === 'function') {
                this.root.append(wrapper);
                if (this.root && typeof this.root.id === 'string') {
                    const el = document.getElementById(this.root.id);
                    if (el) this.labelEl = this.findLabelForHost(el);
                }
            }
            else if (this.root && typeof this.root.get === 'function') {
                const el = this.root.get(0);
                if (el instanceof HTMLElement) {
                    el.appendChild(wrapper);
                    this.labelEl = this.findLabelForHost(el);
                }
            }
            else if (this.root && typeof this.root.id === 'string') {
                const el = document.getElementById(this.root.id);
                if (el) {
                    el.appendChild(wrapper);
                    this.labelEl = this.findLabelForHost(el);
                } else {
                    document.body.appendChild(wrapper);
                }
            }
            else {
                document.body.appendChild(wrapper);
            }
        } catch {
            document.body.appendChild(wrapper);
        }

        this.wrapperEl = wrapper;
        this.setEmpty();
    }

    private findLabelForHost(host: HTMLElement): HTMLElement | null {
        try {
            let el: HTMLElement | null = host;
            while (el) {
                if (el.classList && el.classList.contains('field')) {
                    const label = el.querySelector('label.caption, label') as HTMLElement | null;
                    return label;
                }
                el = el.parentElement;
            }
        } catch { /* ignore */ }
        return null;
    }

    private setEmpty(): void {
        this.imgEl.src = '';
        this.imgEl.style.display = 'none';
        if (this.wrapperEl) this.wrapperEl.style.display = 'none';
        if (this.labelEl) this.labelEl.style.display = 'none';
    }

    private setDataUri(dataUri: string): void {
        if (!dataUri) {
            this.setEmpty();
            return;
        }
        this.imgEl.src = dataUri;
        this.imgEl.style.display = '';
        if (this.wrapperEl) this.wrapperEl.style.display = 'flex';
        if (this.labelEl) this.labelEl.style.display = '';
    }

    // Accepts:
    //  - data URI (starts with "data:")
    //  - raw base64 payload (we'll assume PNG and wrap)
    //  - object with { Base64: "...", MimeType: "image/jpeg" } or similar
    private toDataUri(value: any): string | null {
        if (!value && value !== 0) return null;

        // If value is object containing base64 or dataUri
        if (typeof value === 'object') {
            if (value.DataUri && typeof value.DataUri === 'string') return value.DataUri;
            if (value.Base64 && typeof value.Base64 === 'string') {
                const mime = value.MimeType || value.ContentType || 'image/png';
                return `data:${mime};base64,${value.Base64}`;
            }
            // try common property names
            if (value.Image && typeof value.Image === 'string') {
                // may be data uri or base64
                return this.toDataUri(value.Image);
            }
        }

        const s = String(value).trim();
        if (!s) return null;

        // already a data uri
        if (s.startsWith('data:')) return s;

        // looks like base64 (may contain newlines, spaces) -> strip whitespace then wrap
        // crude test: length large and chars in base64 alphabet
        const stripped = s.replace(/\s+/g, '');
        const base64Regex = /^[A-Za-z0-9+/=]+$/;
        if (stripped.length > 50 && base64Regex.test(stripped)) {
            return `data:image/png;base64,${stripped}`;
        }

        // if appears to be url (starts with http or /), return as-is (useful for already hosted images)
        if (/^(https?:)?\/\//i.test(s) || s.startsWith('/')) {
            return s;
        }

        // fallback: treat as base64 anyway
        return `data:image/png;base64,${encodeURIComponent(stripped)}`;
    }

    // Serenity editor API
    public set_value(v: any): void {
        const dataUri = this.toDataUri(v);
        this.setDataUri(dataUri);
    }

    public get_value(): any {
        return this.imgEl && this.imgEl.src ? this.imgEl.src : '';
    }

    // property accessors
    public set value(v: any) { this.set_value(v); }
    public get value(): any { return this.get_value(); }
}